import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/providers/AuthProvider';
import { useCreditBalance } from '@/api/hooks/useCredit';
import { useCreditPolicy, useCreditTransactions, useTopUpCredits } from '@/api/hooks/useCredits';
import { useTenantBySlug } from '@/api/hooks/useTenantBySlug';
import { DEFAULT_TENANT_SLUG } from '@/lib/constants';
import { formatCurrency } from '@/lib/format';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
// Separator available if needed
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Wallet, Loader2 } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { STRIPE_PUBLISHABLE_KEY } from '@/lib/constants';
import { toast } from 'sonner';

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

function CreditsContent() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const stripe = useStripe();
  const elements = useElements();
  const { data: creditData } = useCreditBalance(isAuthenticated);
  const { data: tenant } = useTenantBySlug(DEFAULT_TENANT_SLUG);
  const tenantId = tenant?.id;
  const { data: policy, isLoading: policyLoading } = useCreditPolicy(tenantId);
  const { data: txData } = useCreditTransactions();
  const topUpMutation = useTopUpCredits();
  const [selectedTier, setSelectedTier] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const balance = creditData?.balance || 0;
  const transactions = txData?.transactions ?? [];

  const handleTopUp = async () => {
    if (selectedTier == null || !policy || !stripe || !elements || !tenantId) return;
    const tier = policy.tiers[selectedTier];
    setIsProcessing(true);

    try {
      const cardElement = elements.getElement(CardElement);
      if (!cardElement) return;

      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card', card: cardElement,
      });
      if (stripeError) {
        toast.error(stripeError.message || t('booking.card_declined'));
        setIsProcessing(false);
        return;
      }

      await topUpMutation.mutateAsync({
        tenantId,
        amount: tier.amount,
        bonusPercent: tier.bonusPercent,
        paymentMethodId: paymentMethod.id,
        tierName: tier.name,
      });

      toast.success(t('credit.topup_success', { amount: formatCurrency(tier.totalCredit, 'CAD', i18n.language) }));
      setSelectedTier(null);
    } catch {
      toast.error(t('credit.topup_failed'));
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 px-4 py-6">
      <h1 className="text-2xl font-semibold">{t('credit.credits_title')}</h1>

      {/* Current Balance */}
      <Card className="border-l-4 border-l-primary">
        <CardContent className="flex items-center gap-4 p-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Wallet className="h-6 w-6 text-primary" />
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{t('credit.current_balance')}</p>
            <p className="text-3xl font-bold font-mono">{formatCurrency(balance, 'CAD', i18n.language)}</p>
          </div>
        </CardContent>
      </Card>

      {/* Top-up Tiers */}
      {policyLoading ? (
        <Skeleton className="h-40" />
      ) : policy?.enabled && policy.tiers.length > 0 ? (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold">{t('credit.top_up')}</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            {policy.tiers.map((tier, i) => (
              <Card
                key={tier.name}
                className={`cursor-pointer transition-all ${selectedTier === i ? 'ring-2 ring-primary' : 'hover:border-primary/50'}`}
                onClick={() => setSelectedTier(i)}
              >
                <CardContent className="p-4 text-center">
                  <p className="text-sm font-medium text-muted-foreground">{tier.name}</p>
                  <p className="text-2xl font-bold mt-1">{formatCurrency(tier.amount, 'CAD', i18n.language)}</p>
                  <Badge variant="secondary" className="mt-2 bg-green-100 text-green-800">
                    +{tier.bonusPercent}% {t('credit.bonus')}
                  </Badge>
                  <p className="text-sm font-mono mt-2 text-primary font-semibold">
                    {t('credit.get')} {formatCurrency(tier.totalCredit, 'CAD', i18n.language)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>

          {selectedTier !== null && (
            <Card>
              <CardContent className="p-4 space-y-3">
                <p className="text-sm font-medium">{t('credit.pay_with_card')}</p>
                <div className="rounded-md border p-3">
                  <CardElement options={{
                    style: { base: { fontSize: '16px', color: '#1a1a1a', '::placeholder': { color: '#a3a3a3' } } },
                    hidePostalCode: true,
                  }} />
                </div>
                <Button className="w-full" disabled={isProcessing || !stripe} onClick={handleTopUp}>
                  {isProcessing ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t('credit.processing')}</>
                  ) : (
                    t('credit.buy_now', { amount: formatCurrency(policy.tiers[selectedTier].amount, 'CAD', i18n.language) })
                  )}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      ) : null}

      {/* Transaction History */}
      {transactions.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">{t('credit.transaction_history')}</h2>
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {transactions.map((tx) => (
                  <div key={tx.id} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm font-medium">{tx.description || tx.reason}</p>
                      <p className="text-xs text-muted-foreground">{new Date(tx.createdAt).toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className={`font-mono font-semibold ${tx.type === 'CREDIT' ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.type === 'CREDIT' ? '+' : '-'}{formatCurrency(tx.amount, 'CAD', i18n.language)}
                      </p>
                      <p className="text-xs text-muted-foreground">{t('credit.balance')}: {formatCurrency(tx.balanceAfter, 'CAD', i18n.language)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export default function CreditsPage() {
  const { i18n } = useTranslation();
  return (
    <Elements stripe={stripePromise} options={{ locale: i18n.language === 'fr' ? 'fr' : 'en' }}>
      <CreditsContent />
    </Elements>
  );
}
